import { type ReactNode, type RefObject, useRef } from 'react';
import { Play, Pause } from 'lucide-react';
import {
  useYouTubeTracker,
  type YTControls,
} from '@/hooks/use-youtube-tracker';

// Trapped mode (default) strips every YouTube affordance that exposes the
// "watch on YouTube" link or related-videos surface; play/pause is delegated
// to the click-shield overlay + the YouTube IFrame API.
// Native mode is opt-in (see /comunicados, /game-rules) and lets the user
// scrub, fullscreen, change speed, and open in YouTube directly.
const TRAPPED_PARAMS = [
  'modestbranding=1',
  'iv_load_policy=3',
  'disablekb=1',
  'fs=0',
  'controls=0',
];

function buildEmbedUrl(id: string, native: boolean): string {
  const origin = encodeURIComponent(window.location.origin);
  const base = ['enablejsapi=1', `origin=${origin}`, 'rel=0', 'playsinline=1'];
  const params = (native ? base : base.concat(TRAPPED_PARAMS)).join('&');
  return `https://www.youtube-nocookie.com/embed/${id}?${params}`;
}

const ALLOW_BASE =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope';
const IFRAME_TRAPPED = 'pointer-events-none aspect-video w-full rounded-lg';
const IFRAME_NATIVE = 'aspect-video w-full rounded-lg';

function VideoIframe(props: {
  id: string;
  innerRef: RefObject<HTMLIFrameElement | null>;
  native: boolean;
}): ReactNode {
  return (
    <iframe
      ref={props.innerRef}
      className={props.native ? IFRAME_NATIVE : IFRAME_TRAPPED}
      src={buildEmbedUrl(props.id, props.native)}
      allow={props.native ? `${ALLOW_BASE}; fullscreen` : ALLOW_BASE}
      allowFullScreen={props.native}
      title="Video"
    />
  );
}

const OVERLAY_CLS =
  'absolute inset-0 flex items-center justify-center rounded-lg transition-opacity';
const ICON_WRAP =
  'flex h-16 w-16 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-md sm:h-20 sm:w-20';

function ClickShield({ controls }: { controls: YTControls }): ReactNode {
  const visible = !controls.isPlaying;
  return (
    <button
      type="button"
      onClick={controls.togglePlay}
      aria-label={controls.isPlaying ? 'Pausar' : 'Reproducir'}
      className={`${OVERLAY_CLS} ${visible ? 'bg-black/30 opacity-100' : 'opacity-0 hover:opacity-100'}`}
    >
      <span className={ICON_WRAP}>
        {controls.isPlaying ? <Pause size={28} /> : <Play size={28} />}
      </span>
    </button>
  );
}

type FrameProps = {
  videoId: string;
  threshold: number;
  onWatched: () => void;
  nativeControls?: boolean;
  onProgress?: (elapsed: number, duration: number) => void;
};

export function PlayerFrame(props: FrameProps): ReactNode {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const controls = useYouTubeTracker({
    iframeRef,
    enabled: true,
    threshold: props.threshold,
    onWatched: props.onWatched,
    ...(props.onProgress && { onProgress: props.onProgress }),
  });
  const native = props.nativeControls ?? false;
  return (
    <div className="relative w-full">
      <VideoIframe id={props.videoId} innerRef={iframeRef} native={native} />
      {!native && <ClickShield controls={controls} />}
    </div>
  );
}
